import { AsyncPipe } from "@angular/common";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnInit,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  catchError,
  combineLatestWith,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from "rxjs";

import { DataService } from "../services/data.service";
import { QPilotCloudApiService } from "../services/qpilot-cloud-api.service";
import { SiteSettingsService } from "../services/site-settings.service";
import { errorSnackBar, successSnackBar } from "../utils";
import { ChangePaymentMethodComponent } from "../web-components/change-payment-method/change-payment-method.component";
import {
  ChangePaymentEvent,
  ChangePaymentAndProcessNowEvent,
  ResumeOrderEvent,
  RescheduleOrderEvent,
} from "../web-components/types/events";
import { QPilotPaymentMethod } from "../web-components/types/payment-method";
import {
  QPilotScheduledOrder,
  QPilotScheduledOrderTotals,
  OrderStatus,
} from "../web-components/types/scheduled-order";
import { DeliveryService } from "../services/delivery.service";

type ScheduledOrderWithTotalsCallback = QPilotScheduledOrder & {
  getOrderTotals: () => Observable<QPilotScheduledOrderTotals>;
};

@Component({
  selector: "app-update-payment",
  standalone: true,
  imports: [AsyncPipe, ChangePaymentMethodComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./update-payment.component.html",
  styleUrl: "./update-payment.component.scss",
})
export class UpdatePaymentComponent implements OnInit {
  order$: Observable<ScheduledOrderWithTotalsCallback>;
  orderVerboseName: string;
  paymentMethods$: Observable<QPilotPaymentMethod[]>;
  paymentMethodsUrl: string;

  constructor(
    private dataService: DataService,
    private qPilotCloudApiService: QPilotCloudApiService,
    private siteSettingsService: SiteSettingsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    protected deliveryService: DeliveryService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      order: Observable<ScheduledOrderWithTotalsCallback>;
    },
  ) {}

  ngOnInit() {
    this.order$ = this.data.order;
    this.orderVerboseName = this.siteSettingsService.orderVerboseName;
    this.paymentMethodsUrl = this.siteSettingsService.paymentMethodsPageUrl;
    this.paymentMethods$ =
      this.qPilotCloudApiService.getPaymentMethodsForCustomer();
  }

  onCancel() {
    this.dialog.closeAll();
  }

  updateNOD(event: RescheduleOrderEvent) {
    this.deliveryService.trackOrder(event.orderId);
    if (this.siteSettingsService.useNextime)
      this.deliveryService.isLoading = true;

    this.qPilotCloudApiService
      .updateScheduledOrderNextOccurrence(
        event.orderId,
        new Date(event.nextOccurrenceUtc),
      )
      .pipe(
        tap((order) => {
          if (this.siteSettingsService.useNextime) {
            this.deliveryService.setShouldFetchNextOccurrenceDate(false);
          }
          this.dataService.updateOrderNextOccurrenceAndDeliveryInfo(
            order.id,
            order.nextOccurrenceUtc,
            undefined,
          );
        }),
        tap(() =>
          successSnackBar(this.snackBar, "Update: Next Order Date Changed"),
        ),
        catchError(() => {
          errorSnackBar(
            this.snackBar,
            "Update Failed: Error Updating Next Order Date",
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  onApply({ scheduledOrderId: orderId, paymentMethodId }: ChangePaymentEvent) {
    this.qPilotCloudApiService
      .updateScheduledOrderPaymentMethod(orderId, paymentMethodId)
      .pipe(
        tap((result) =>
          this.dataService.updateOrderPayment(result.id, result.paymentMethod),
        ),
        tap(() => successSnackBar(this.snackBar, "Payment updated")),
        tap(() => this.onCancel()),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error updating Payment");
          return of(null);
        }),
      )
      .subscribe();
  }

  onApplyAndProcess({
    scheduledOrderId: orderId,
    paymentMethodId,
  }: ChangePaymentAndProcessNowEvent) {
    this.order$
      .pipe(
        take(1),
        combineLatestWith(
          this.qPilotCloudApiService.updateScheduledOrderPaymentMethod(
            orderId,
            paymentMethodId,
          ),
        ),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error updating payment");
          return of();
        }),
        switchMap(([order, result]) =>
          this.qPilotCloudApiService.retryProcessing(result.id).pipe(
            tap((updatedOrder) =>
              this.dataService.updateOrder({
                ...order,
                paymentMethod: result.paymentMethod,
                paymentMethodId: result.paymentMethod?.id,
                status: updatedOrder.status.toLowerCase() as OrderStatus,
                lastOccurrenceUtc: updatedOrder.lastOccurrenceUtc,
                nextOccurrenceUtc: updatedOrder.nextOccurrenceUtc,
              }),
            ),
            catchError(() => {
              errorSnackBar(this.snackBar, "Failed: Error processing");
              return of();
            }),
          ),
        ),
        tap(() => {
          this.dialog.closeAll();
          successSnackBar(
            this.snackBar,
            "Payment updated and processing retried",
          );
        }),
      )
      .subscribe();
  }

  onResumeOrder(ev: ResumeOrderEvent) {
    this.qPilotCloudApiService
      .safeActivateScheduledOrder(ev.orderId)
      .pipe(
        tap(() => this.dataService.resumeOrder(ev.orderId)),
        tap(() => successSnackBar(this.snackBar, "Order reactivated")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error reactivating Order");
          return of(null);
        }),
      )
      .subscribe();
  }
}
